import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input } from '@angular/core';
import { Distributor } from '@wsphere/distributors/domain';
import { CommissionPlanSelection } from '../../components';
import { Cell, CommissionService, Element } from '../../services';
import {
  computeDistributorCommissionReport,
  DistributorCommissionReport,
  GroupedCommissionBreakdown,
  GroupedCommissionRows,
  groupBreakdownByFeeType,
  groupRowsByFeeType,
} from './compute-commission-report';

// Same normalization convention used server-side to locate system dimensions (Seller, Fee Type) by title.
function normalizeDimensionTitle(value: string): string {
  return value.toLowerCase().replace(/[\s\-_/]+/g, '');
}

export function isTermElement(element: Element | undefined): element is Element {
  return !!element?.dimension?.isSystem && normalizeDimensionTitle(element.dimension.title) === 'term';
}

export function resolveTermElements(cells: Cell[]): Element[] {
  const byId = new Map<string, Element>();

  for (const cell of cells) {
    for (const cellElement of cell.elements ?? []) {
      if (isTermElement(cellElement.element)) byId.set(cellElement.element.id, cellElement.element);
    }
  }

  return [...byId.values()].sort((a, b) => a.title.localeCompare(b.title));
}

// Same idea as seller scoping: keep term-agnostic cells plus ones matching the selected term.
export function filterByTerm(cells: Cell[], selectedTermId: string | null): Cell[] {
  if (!selectedTermId) return cells;

  return cells.filter(cell => {
    const termElementIds = (cell.elements ?? [])
      .map(cellElement => cellElement.element)
      .filter(isTermElement)
      .map(element => element.id);

    if (termElementIds.length === 0) return true;
    return termElementIds.includes(selectedTermId);
  });
}

@Component({
  selector: 'ws-distributor-commission-page',
  templateUrl: './commission.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DistributorCommissionPage {
  private commissionSvc = inject(CommissionService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @Input() distributor: Distributor.Model | null = null;

  protected loading = false;
  protected error = false;
  protected selection: CommissionPlanSelection | null = null;
  protected report: DistributorCommissionReport | null = null;
  protected groupedBreakdown: GroupedCommissionBreakdown[] = [];
  protected groupedRows: GroupedCommissionRows[] = [];

  protected termElements: Element[] = [];
  protected selectedTermId: string | null = null;

  private allCells: Cell[] = [];
  private requestGeneration = 0;

  async onPlanSelected(selection: CommissionPlanSelection | null) {
    const generation = ++this.requestGeneration;

    this.selection = selection;
    this.error = false;
    this.report = null;
    this.groupedBreakdown = [];
    this.groupedRows = [];
    this.allCells = [];
    this.termElements = [];
    this.selectedTermId = null;

    if (!selection || !this.distributor) return;

    this.loading = true;
    this.changeDetectorRef.markForCheck();

    try {
      const response = await this.commissionSvc.listCells(this.distributor.id, selection.planId);
      if (generation !== this.requestGeneration) return; // a newer selection superseded this call

      this.allCells = response.items;
      this.termElements = resolveTermElements(this.allCells);
      this.rebuildReport();
    } catch (err) {
      if (generation !== this.requestGeneration) return;
      this.error = true;
      console.error('Failed to load commission rates', err);
    } finally {
      if (generation === this.requestGeneration) {
        this.loading = false;
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  protected onTermChange(termId: string | readonly string[] | null) {
    this.selectedTermId = typeof termId === 'string' ? termId : null;
    this.rebuildReport();
  }

  private rebuildReport() {
    this.report = computeDistributorCommissionReport(filterByTerm(this.allCells, this.selectedTermId));
    this.groupedBreakdown = groupBreakdownByFeeType(this.report.breakdown);
    this.groupedRows = groupRowsByFeeType(this.report.rows);
  }
}
